import type { CountedStockResource, RecordTillPayoutDto } from '@repo/api-client';
import { z } from 'zod';

import { isRepresentablePaise, paiseFromRupees, RUPEES_SHAPE } from '@/lib/money';

import { labelForPaymentMode, SALE_PAYMENT_MODES } from '../sales/payment-modes';

/**
 * What the two Day Close dialogs accept, and the three readings the
 * reconciliation view must not get wrong.
 *
 * Kept beside the screen rather than in `lib/field-schemas.ts` for the reason
 * `expense-form.ts` gives about its own amount: **the two money fields on this
 * screen have different floors, and sharing a fragment would mean loosening one
 * of them.** A counted drawer of zero is a real day and the API accepts it
 * (FR-618); a payout of zero is refused `422 DAY_CLOSE_PAYOUT_AMOUNT_INVALID`
 * (FR-623). One `priceInRupeesSchema` across both would either refuse an empty
 * drawer or accept a payout of nothing, and neither is a preference to settle
 * — they are two different refusals on the server.
 *
 * Nothing here words a refusal that came back from the API. Six `DAY_CLOSE_*`
 * codes have copy in `error-messages.ts`, and that is the only thing in this app
 * that turns a code into a sentence. What is written below is the *field* copy —
 * what this form refuses before the wire — which is a different job.
 */

/* -------------------------------------------------------------------------- */
/* The published bounds                                                        */
/* -------------------------------------------------------------------------- */

/**
 * `CloseBusinessDayDto.countedCashPaise` is `minimum: 0, maximum: 1000000000`,
 * and `RecordTillPayoutDto.amountPaise` is `minimum: 1, maximum: 1000000000`.
 *
 * **The floors differ and the ceiling does not**, which is the whole reason
 * these are two schemas. Read off the committed document rather than inferred
 * from the refusal copy, which states the ceiling in rupees.
 */
const MAX_PAISE = 1_000_000_000;
const MIN_PAYOUT_PAISE = 1;

/** `maxLength: 500` on both reason fields, published on both DTOs. */
const REASON_MAX = 500;

const RUPEES_CEILING = new Intl.NumberFormat('en-IN').format(MAX_PAISE / 100);

/* -------------------------------------------------------------------------- */
/* Counting the drawer                                                         */
/* -------------------------------------------------------------------------- */

export const COUNTED_CASH_REQUIRED =
  'Enter what was counted in the drawer, in rupees. Zero is an answer — type 0 for an empty drawer.';

/**
 * The minus sign is named explicitly, and it is the mistake worth naming.
 *
 * The operator submits **what they counted**, never a difference (FR-616), so
 * somebody thinking in variances reaches for a sign that has no meaning on this
 * field. The message says what the field is for rather than only what it
 * refuses.
 */
export const COUNTED_CASH_MALFORMED =
  'A counted amount is rupees and paise — 12400 or 12400.50. No ₹, no commas, no minus sign, and no more than two decimal places. Count the drawer, not the difference.';

export const COUNTED_CASH_TOO_LARGE = `A counted amount is at most ₹${RUPEES_CEILING}. Check the figure.`;

export const COUNTED_CASH_UNREPRESENTABLE =
  'That amount is larger than this screen can carry without rounding it. Check the figure.';

/**
 * What was physically in the drawer, as a string, because that is what an
 * `<Input>` holds.
 *
 * **There is no lower bound beyond the shape**, and that absence is the ticket's
 * headline made mechanical: a count of ₹0 closes a day, a count far above the
 * takings closes a day, and nothing in this app may invent the threshold the API
 * deliberately does not have (CL-601, FR-619a). The only things refused here are
 * a shape the wire cannot carry and a figure past the published ceiling — the
 * same two `DAY_CLOSE_COUNT_INVALID` covers, said at the field instead of after
 * a round trip.
 *
 * Not `z.coerce.number()`, for the reason `expense-form.ts` gives: coercion
 * reads `''` as `0`, and on *this* field zero is valid — so an untouched form
 * would silently close the day on an empty drawer.
 */
const countedCashSchema = z
  .string()
  .trim()
  .min(1, COUNTED_CASH_REQUIRED)
  .regex(RUPEES_SHAPE, COUNTED_CASH_MALFORMED)
  .refine((rupees) => isRepresentablePaise(paiseFromRupees(rupees)), COUNTED_CASH_UNREPRESENTABLE)
  .refine((rupees) => paiseFromRupees(rupees) <= MAX_PAISE, COUNTED_CASH_TOO_LARGE);

export const closeDaySchema = z.object({ countedCash: countedCashSchema });

export type CloseDayValues = z.infer<typeof closeDaySchema>;

/* -------------------------------------------------------------------------- */
/* Reopening                                                                   */
/* -------------------------------------------------------------------------- */

export const REOPEN_REASON_REQUIRED =
  'Say why the day is being reopened. It undoes a close somebody signed off, and this is what explains that afterwards.';

/**
 * A reason of spaces, told apart from an empty field.
 *
 * The API refuses both as `DAY_CLOSE_REASON_INVALID` and they are one refusal
 * there. They are two different things to say to a person: one field has not
 * been filled in, and the other looks filled in. Splitting the *field* message
 * does not re-open any enumeration — the server's non-distinction is about
 * failures a caller could probe with, and this is about a caret.
 */
export const REOPEN_REASON_BLANK =
  'A reason of spaces is not a reason. Say what was wrong with the close — the earlier count is kept either way, and this is the only account of why it was set aside.';

export const REOPEN_REASON_TOO_LONG = `A reason is at most ${String(REASON_MAX)} characters.`;

/**
 * Two checks and not one, and the value is **not** trimmed on the way out.
 *
 * `.min(1)` on the raw string catches the empty field; the trimmed check catches
 * a field holding only whitespace. Trimming first would collapse them into one
 * message. What leaves this form is what was typed, because the API stores it
 * exactly as submitted and the event cannot afterwards be edited.
 */
const reopenReasonSchema = z
  .string()
  .min(1, REOPEN_REASON_REQUIRED)
  .refine((value) => value.trim().length > 0, REOPEN_REASON_BLANK)
  .max(REASON_MAX, REOPEN_REASON_TOO_LONG);

export const reopenDaySchema = z.object({ reason: reopenReasonSchema });

export type ReopenDayValues = z.infer<typeof reopenDaySchema>;

/* -------------------------------------------------------------------------- */
/* Cash out of the drawer                                                      */
/* -------------------------------------------------------------------------- */

export const PAYOUT_AMOUNT_REQUIRED = 'Enter how much cash left the drawer, in rupees.';

export const PAYOUT_AMOUNT_MALFORMED =
  'An amount is rupees and paise — 800 or 800.50. No ₹, no commas, no minus sign, and no more than two decimal places. Cash leaving is what a payout already means, so there is no sign to add.';

/** **Zero is a refusal here**, unlike on the counted-cash field above. */
export const PAYOUT_AMOUNT_TOO_SMALL =
  'A payout moves at least one paisa. Nothing left the drawer is not a payout.';

export const PAYOUT_AMOUNT_TOO_LARGE = `A payout is at most ₹${RUPEES_CEILING}. Check the figure.`;

export const PAYOUT_AMOUNT_UNREPRESENTABLE = COUNTED_CASH_UNREPRESENTABLE;

export const PAYOUT_REASON_REQUIRED = 'Say what the cash was for.';

export const PAYOUT_REASON_BLANK =
  'A reason of spaces is not a reason. Cash out of the drawer with nothing said about it cannot be traced afterwards, and a payout cannot be edited — a mistake is put right by a second, offsetting one.';

export const PAYOUT_REASON_TOO_LONG = REOPEN_REASON_TOO_LONG;

const payoutAmountSchema = z
  .string()
  .trim()
  .min(1, PAYOUT_AMOUNT_REQUIRED)
  .regex(RUPEES_SHAPE, PAYOUT_AMOUNT_MALFORMED)
  .refine((rupees) => isRepresentablePaise(paiseFromRupees(rupees)), PAYOUT_AMOUNT_UNREPRESENTABLE)
  .refine((rupees) => paiseFromRupees(rupees) >= MIN_PAYOUT_PAISE, PAYOUT_AMOUNT_TOO_SMALL)
  .refine((rupees) => paiseFromRupees(rupees) <= MAX_PAISE, PAYOUT_AMOUNT_TOO_LARGE);

const payoutReasonSchema = z
  .string()
  .min(1, PAYOUT_REASON_REQUIRED)
  .refine((value) => value.trim().length > 0, PAYOUT_REASON_BLANK)
  .max(REASON_MAX, PAYOUT_REASON_TOO_LONG);

export const payoutSchema = z.object({
  amount: payoutAmountSchema,
  reason: payoutReasonSchema,
});

export type PayoutValues = z.infer<typeof payoutSchema>;

/**
 * What this dialog is about to send, as one string.
 *
 * The body's own `id` is deliberately absent: `attemptFor` in
 * `lib/write-attempt.ts` mints it *with* the key from this value, so two
 * attempts that compare equal carry the same id as well as the same key and the
 * raw bodies are byte-identical. Minting the id separately is the trap that
 * module names — a fresh id per press makes every retry a different body, and
 * the replay the header was held for never happens.
 *
 * `JSON.stringify` over an array rather than an object, so the comparison cannot
 * turn on key order.
 */
export function fingerprintOfPayout(
  outletId: string,
  body: Omit<RecordTillPayoutDto, 'id'>,
): string {
  return JSON.stringify([outletId, body.amountPaise, body.reason]);
}

/* -------------------------------------------------------------------------- */
/* Reading the reconciliation                                                  */
/* -------------------------------------------------------------------------- */

/**
 * What was counted on the shelves, as three answers rather than two figures.
 *
 * **`NOT_APPLICABLE` is not a count of zero**, and the difference is the whole
 * of FR-632: the gate carries no stock, so `0 / 0` on its sheet reads as a
 * reconciled stocktake when nothing was counted at all. Modelled as a union so
 * the screen cannot render a number that does not exist — there is no field to
 * reach for on the two answers that have none.
 */
export type CountedStock =
  /** This outlet carries no stock (CL-103). There was nothing to count. */
  | { readonly kind: 'not-applicable' }

  /** It does carry stock, and nobody counted it that day. */
  | { readonly kind: 'not-counted' }

  /** A stocktake exists, and these are its figures. */
  | {
      readonly kind: 'counted';
      readonly stocktakeCount: number;
      readonly itemsWithVariance: number;
    };

export function countedStockOf(resource: CountedStockResource): CountedStock {
  if (resource.applicability === 'NOT_APPLICABLE') return { kind: 'not-applicable' };

  if (resource.applicability === 'NOT_COUNTED') return { kind: 'not-counted' };

  const { stocktakeCount, itemsWithVariance } = resource;

  // Both are documented as non-null whenever `COUNTED`, so this branch is a
  // contract violation rather than a state. It is read as "nobody counted"
  // rather than as zero for the same reason `NOT_APPLICABLE` is: of the two
  // wrong answers available, "everything reconciled" is the one somebody acts on.
  if (stocktakeCount === null || itemsWithVariance === null) return { kind: 'not-counted' };

  return { kind: 'counted', stocktakeCount, itemsWithVariance };
}

/**
 * The difference between the drawer and the books, as an answer rather than a
 * number.
 *
 * **Null is the state, not missing data.** `variancePaise` is null for exactly
 * as long as `countedPaise` is — while the day is open, including a day that has
 * been reopened — and a screen that read null as zero would report a day with no
 * shortfall before anybody had opened the drawer.
 *
 * Zero and not-counted are kept apart for the same reason: "counted, and it came
 * out exact" is a fact somebody has established, and "not counted yet" is not.
 *
 * The sign is the API's, restated here because it is not the intuitive one:
 * **negative is a shortfall** — less in the drawer than the books expected —
 * and positive is a surplus (FR-619). The magnitude is published as a positive
 * number here so the screen never has to decide what to do with a minus sign it
 * has already turned into a word.
 */
export type Variance =
  /** The day is open. There is no count yet, which is not a variance of zero. */
  | { readonly kind: 'not-counted' }

  /** Counted, and it matched. */
  | { readonly kind: 'exact' }

  /** Less in the drawer than expected, by this many paise. */
  | { readonly kind: 'short'; readonly byPaise: number }

  /** More in the drawer than expected, by this many paise. */
  | { readonly kind: 'over'; readonly byPaise: number };

export function varianceOf(variancePaise: number | null): Variance {
  if (variancePaise === null) return { kind: 'not-counted' };

  if (variancePaise === 0) return { kind: 'exact' };

  return variancePaise < 0
    ? { kind: 'short', byPaise: -variancePaise }
    : { kind: 'over', byPaise: variancePaise };
}

/**
 * How a rung-up line was paid, as a person reads it.
 *
 * **Not `labelForPaymentMode` directly**, and the two differences are both
 * facts about this resource rather than styling:
 *
 * - `RungUpLineResource.paymentMode` is `string | null`, and **null means
 *   unknown** — a back-office entry has no mode, and a ticket's had none before
 *   API #216. It is a recorded fact rather than missing data, and the document
 *   is explicit that "null is never read as cash: nothing in this system turns
 *   an unknown mode into a cash figure somebody would then be asked to account
 *   for." So it gets a label of its own and that label is not "Cash".
 * - It is a `string` and not the closed union the sales list holds, so a fourth
 *   mode added to the API arrives here as itself. It is rendered rather than
 *   swallowed, because swallowing a line loses money off a reconciliation — the
 *   one screen where the figures have to add up to what is in a drawer.
 */
export function labelForRungUpMode(mode: string | null): string {
  if (mode === null) return 'Not recorded';

  const known = SALE_PAYMENT_MODES.find((candidate) => candidate === mode);

  return known === undefined ? mode : labelForPaymentMode(known);
}
